import { ContextKeys, DateIncludedInAccountingQuery, NrbValidator } from '@skylab-shared/business';
import { BusinessObjectPart, BusinessObjectPartContext, ModelBase, QueryBus, Weaver } from '@skylab-shared/core';
import { DocumentClientModel, ExchangeRateModel, PurchaseBankAccountModel, SplitPaymentState } from '@skylab-shared/dataModel';
import { dateDiffInDays } from '@skylab-shared/utils';
import { PurchaseDocumentUniqueOriginalNumberQuery } from '../queries/purchase-document-unique-original-number.query';
import { PurchaseDatePeriodInAccountancyWarning } from '../violations/purchase-date-period-in-accountancy-warning';
import { PurchaseEmptyClientError } from '../violations/purchase-empty-client-error';
import { PurchaseEmptyDocumentOrginalNumberError } from '../violations/purchase-empty-document-orginal-number-error';
import { PurchaseInvalidBankAccountNumberWarning } from '../violations/purchase-invalid-bank-account-number-warning';
import { PurchaseIsUniqueDocumentOrginalNumberWarning } from '../violations/purchase-is-unique-document-orginal-number-warning';
import { PurchaseReceiptDateEarlierThanIssueDateError } from '../violations/purchase-receipt-date-earlier-than-issue-date-error';

interface PurchaseModelPart extends ModelBase {
    currencyId: number;
    exchangeRate: ExchangeRateModel;
    seller: DocumentClientModel;
    externalDocumentNumber: string;
    issueDate: Date;
    receiptDate: Date;
    vatDeductionDate: Date;
    id: number;
    communityTransactionId: number;
    paymentMethodId: number;
    splitPayment: SplitPaymentState;
    originalEInvoiceDocumentId?: number | null;
}

export class PurchaseDocumentValidationBOPart extends BusinessObjectPart<PurchaseModelPart> {
    public constructor(
        weaver: Weaver,
        private readonly queryBus: QueryBus
    ) {
        super(weaver);
    }
    public override async initializeAsync(context: BusinessObjectPartContext<PurchaseModelPart>): Promise<void> {
        await super.initializeAsync(context);
        this.registerValidationRules();
    }

    private registerValidationRules(): void {
        this.registerNotEmptyClientRule();
        this.registerDatePeriodInAccountancyRule();
        this.registerNotEmptyDocumentOrginalNumberRule();
        this.registerExistingOrginalNumberRule();
        this.registerReceiptDateEarlierThanIssueDateRule();
        this.registerInvalidBankAccountNumberRule();
    }

    private async isDateIncludedInAccountancyAsync(date: Date): Promise<boolean> {
        return await this.bo.localContext
            .getAsync(ContextKeys.dateIncludedInAccounting(date),
            () => this.queryBus.queryAsync(new DateIncludedInAccountingQuery(date)));
    }

    private registerNotEmptyClientRule(): void {
        this.engine.validation.addRule(
            [[this.modelType, 'seller']],
            ctx => ctx.source.seller !== null,
            PurchaseEmptyClientError
        );
    }

    private registerDatePeriodInAccountancyRule(): void {
        this.engine.validation.addRule(
            [[this.modelType, 'receiptDate'], [this.modelType, 'vatDeductionDate']],
            [[this.modelType, 'vatDeductionDate']],
            async ctx => {
                if (!ctx.source.vatDeductionDate || !ctx.source.receiptDate) return true;
                if (dateDiffInDays(ctx.source.receiptDate, ctx.source.vatDeductionDate) === 0) {
                    return !(await this.isDateIncludedInAccountancyAsync(ctx.source.receiptDate));
                } else {
                    const receiptDateIncluded = await this.isDateIncludedInAccountancyAsync(ctx.source.receiptDate);
                    const vatDeductionDateIncluded = await this.isDateIncludedInAccountancyAsync(ctx.source.vatDeductionDate);
                    return !(receiptDateIncluded || vatDeductionDateIncluded);
                }
            },
            PurchaseDatePeriodInAccountancyWarning
        );
    }

    private registerNotEmptyDocumentOrginalNumberRule(): void {
        this.engine.validation.addRule(
            [[this.modelType, 'externalDocumentNumber']],
            ctx => !!(ctx.source.externalDocumentNumber || '').trim(),
            PurchaseEmptyDocumentOrginalNumberError
        );
    }

    private registerInvalidBankAccountNumberRule(): void {
        this.engine.validation.addRule(
            [[PurchaseBankAccountModel, 'number']],
            ctx => NrbValidator.isValid(ctx.source.number),
            PurchaseInvalidBankAccountNumberWarning
        );
    }

    private registerReceiptDateEarlierThanIssueDateRule(): void {
        this.engine.validation.addRule(
            [[this.modelType, 'issueDate'], [this.modelType, 'receiptDate']],
            [[this.modelType, 'receiptDate']],
            ctx => {
                if (!ctx.source.receiptDate || !ctx.source.issueDate) return false;
                return dateDiffInDays(ctx.source.receiptDate, ctx.source.issueDate) >= 0;
            },
            PurchaseReceiptDateEarlierThanIssueDateError
        );
    }

    private registerExistingOrginalNumberRule(): void {
        this.engine.validation.addRule(
            [[this.modelType, 'externalDocumentNumber'], [this.modelType, 'seller']],
            [[this.modelType, 'externalDocumentNumber']],
            async ctx => {
                const targetId = this.bo.model.originalEInvoiceDocumentId || ctx.source.id;
                if (ctx.source.externalDocumentNumber && ctx.source.externalDocumentNumber.trim().length > 0 && ctx.source.seller && ctx.source.seller.name.trim().length > 0) {
                    const query = new PurchaseDocumentUniqueOriginalNumberQuery(
                        {
                            requestParams: {
                                clientName: ctx.source.seller.name,
                                externalDocumentNumber: ctx.source.externalDocumentNumber
                            },
                            queryParams: {
                                documentId: targetId
                            }
                        });
                    return (await this.queryBus.queryAsync(query));
                }
                return await Promise.resolve(true);
            },
            PurchaseIsUniqueDocumentOrginalNumberWarning
        );
    }
}
